import React from "react";
import { ContextMenu, MenuItem } from "../common/Menu";

interface Props {
  title: string;
  // A back affordance is shown whenever this is provided. Omit it on a root
  // screen that has nowhere to go back to.
  onBack?: () => void;
  // Optional context line under the title (e.g. a breadcrumb trail).
  subtitle?: string;
  /**
   * Commands for whatever this screen is showing. Given a non-empty list the
   * header grows a trailing "⋯" — so a rule can be acted on from the screen
   * you're editing it in, not only from the list you reached it through.
   */
  menuItems?: MenuItem[];
}

// Shared top bar for every mobile drill-down screen: a back chevron on the
// left and the current screen's title. Kept in one place so the app-level
// shell and the elements drill-down look identical.
const MobileHeader: React.FC<Props> = ({ title, onBack, subtitle, menuItems }) => {
  const [menuAnchor, setMenuAnchor] = React.useState<{ x: number; y: number } | null>(null);
  const hasMenu = !!menuItems && menuItems.length > 0;

  return (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "4px",
      padding: "8px 12px",
      borderBottom: "1px solid #e0e0e0",
      background: "#fafafa",
      flexShrink: 0,
      minHeight: "44px",
    }}
  >
    {onBack && (
      <button
        onClick={onBack}
        aria-label="Back"
        style={{
          border: "none",
          background: "transparent",
          fontSize: "22px",
          lineHeight: 1,
          color: "#1565c0",
          cursor: "pointer",
          padding: "4px 8px",
          marginLeft: "-4px",
        }}
      >
        ‹
      </button>
    )}
    <div style={{ minWidth: 0, flex: 1 }}>
      <div
        style={{
          fontSize: "16px",
          fontWeight: 600,
          color: "#222",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </div>
      {subtitle && (
        <div
          style={{
            fontSize: "11px",
            color: "#888",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {subtitle}
        </div>
      )}
    </div>

    {hasMenu && (
      <button
        type="button"
        aria-label={`Actions for ${title}`}
        onClick={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          setMenuAnchor({ x: rect.right, y: rect.bottom });
        }}
        style={{
          flexShrink: 0,
          border: "none",
          background: "transparent",
          color: "#555",
          font: "inherit",
          fontSize: "20px",
          lineHeight: 1,
          cursor: "pointer",
          padding: "4px 8px",
          marginRight: "-4px",
        }}
      >
        ⋯
      </button>
    )}

    {menuAnchor && menuItems && (
      <ContextMenu anchor={menuAnchor} items={menuItems} touch onClose={() => setMenuAnchor(null)} />
    )}
  </div>
  );
};

export default MobileHeader;
